import React, { useEffect, useMemo, useRef, useState } from "react";
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";
import "@react-pdf-viewer/print/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { getFilePlugin } from "@react-pdf-viewer/get-file";
import { searchPlugin, Match } from "@react-pdf-viewer/search";
import { printPlugin } from "@react-pdf-viewer/print";
import { rotatePlugin } from "@react-pdf-viewer/rotate";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import {
  selectionModePlugin,
  SelectionMode,
} from "@react-pdf-viewer/selection-mode";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import { useModalStyles } from "../useModalStyles";

interface PDFViewerProps {
  base64String: string;
  fileName?: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
  defaultScale?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  base64String,
  fileName,
  isOpen,
  onClose,
  title,
  defaultScale,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    footerSx,
    cancelBtnSx,
  } = useModalStyles();

  const c = {
    toolbarBg: isDark ? "#161b27" : "#ffffff",
    toolbarBorder: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
    text: isDark ? "#e2e8f0" : "#1e293b",
    muted: isDark ? "rgba(226,232,240,0.5)" : "#64748b",
    hover: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    active: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.10)",
    accent: "#14b8a6",
    viewerBg: isDark ? "#141720" : "#e8edf2",
    inputBg: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
    inputBorder: isDark ? "rgba(255,255,255,0.10)" : "#cbd5e1",
    sidebarBg: isDark ? "#161b27" : "#f1f5f9",
    pageShadow: isDark
      ? "0 4px 24px rgba(0,0,0,0.55)"
      : "0 2px 16px rgba(0,0,0,0.10)",
  };

  const getFilePluginInstance = getFilePlugin({
    fileNameGenerator: () => {
      const s = fileName || title || "document";
      return s.endsWith(".pdf") ? s : `${s}.pdf`;
    },
  });
  const { DownloadButton } = getFilePluginInstance;

  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut } = zoomPluginInstance;

  const pageNavPluginInstance = pageNavigationPlugin();
  const { CurrentPageInput, NumberOfPages, GoToPreviousPage, GoToNextPage } =
    pageNavPluginInstance;

  const searchPluginInstance = searchPlugin();
  const { highlight, clearHighlights, jumpToNextMatch, jumpToPreviousMatch } =
    searchPluginInstance;

  const printPluginInstance = printPlugin();
  const { PrintButton } = printPluginInstance;

  const rotatePluginInstance = rotatePlugin();
  const { RotateForwardButton, RotateBackwardButton } = rotatePluginInstance;

  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;

  const selectionModePluginInstance = selectionModePlugin({
    selectionMode: SelectionMode.Text,
  });

  const blobUrl = useMemo(() => {
    try {
      const raw = atob(base64String);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
      return URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
    } catch {
      return null;
    }
  }, [base64String]);

  useEffect(
    () => () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    },
    [blobUrl],
  );

  useEffect(() => {
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 80);
  }, [showSearch]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      clearHighlights();
      setMatchCount(0);
      setCurrentMatch(0);
      return;
    }
    highlight({ keyword: searchQuery, matchCase: false })
      .then((matches: Match[]) => {
        setMatchCount(matches.length);
        setCurrentMatch(matches.length > 0 ? 1 : 0);
      })
      .catch(() => {});
  }, [searchQuery]);

  if (!blobUrl) {
    return <Typography color="error">Failed to load PDF.</Typography>;
  }

  const tbBtn = (active = false) => ({
    color: active ? c.accent : c.muted,
    borderRadius: "8px",
    p: "7px",
    minWidth: 0,
    backgroundColor: active ? c.active : "transparent",
    transition: "all 0.15s",
    "&:hover": { backgroundColor: c.hover, color: c.text },
  });

  const pluginBtnWrap = (active = false) => ({
    "& button": {
      all: "unset" as any,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "34px",
      height: "34px",
      borderRadius: "8px",
      cursor: "pointer",
      color: active ? `${c.accent} !important` : `${c.muted} !important`,
      backgroundColor: active
        ? `${c.active} !important`
        : "transparent !important",
      transition: "all 0.15s",
      "&:hover": {
        backgroundColor: `${c.hover} !important`,
        color: `${c.text} !important`,
      },
      "& svg": { width: "1.15rem", height: "1.15rem" },
    },
  });

  const TbDivider = () => (
    <Divider
      orientation="vertical"
      flexItem
      sx={{ borderColor: c.toolbarBorder, mx: 0.25, my: 0.5 }}
    />
  );

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={isFullscreen ? false : "md"}
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          ...paperSx(isFullscreen),
          maxHeight: isFullscreen ? "100vh" : "96vh",
          height: isFullscreen ? "100vh" : "96vh",
          ...(isFullscreen && { borderRadius: 0, border: "none" }),
        },
      }}
      onClose={onClose}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(c.accent)}>
          <DescriptionOutlinedIcon
            sx={{ color: c.accent, fontSize: "1.25rem" }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.2 }}
          >
            Document preview
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={closeIconSx} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          px: 1.5,
          py: 0.5,
          backgroundColor: c.toolbarBg,
          borderBottom: `1px solid ${c.toolbarBorder}`,
          flexShrink: 0,
          minHeight: "48px",
        }}
      >
        <Tooltip title={showSidebar ? "Hide thumbnails" : "Show thumbnails"}>
          <IconButton
            size="small"
            onClick={() => setShowSidebar((v) => !v)}
            sx={tbBtn(showSidebar)}
          >
            <ViewSidebarRoundedIcon sx={{ fontSize: "1.15rem" }} />
          </IconButton>
        </Tooltip>

        <TbDivider />

        <GoToPreviousPage>
          {(p: any) => (
            <Tooltip title="Previous page">
              <span>
                <IconButton
                  size="small"
                  onClick={p.onClick}
                  disabled={p.isDisabled}
                  sx={tbBtn()}
                >
                  <NavigateBeforeRoundedIcon sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </GoToPreviousPage>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "& input": {
              width: "38px",
              textAlign: "center",
              backgroundColor: c.inputBg,
              border: `1px solid ${c.inputBorder}`,
              borderRadius: "7px",
              color: c.text,
              fontSize: "0.82rem",
              padding: "4px 2px",
              outline: "none",
              transition: "border-color 0.15s",
              "&:focus": { borderColor: c.accent },
            },
          }}
        >
          <CurrentPageInput />
          <Typography
            sx={{ fontSize: "0.78rem", color: c.muted, userSelect: "none" }}
          >
            /
          </Typography>
          <NumberOfPages>
            {(p: any) => (
              <Typography
                sx={{ fontSize: "0.78rem", color: c.muted, minWidth: "16px" }}
              >
                {p.numberOfPages}
              </Typography>
            )}
          </NumberOfPages>
        </Box>

        <GoToNextPage>
          {(p: any) => (
            <Tooltip title="Next page">
              <span>
                <IconButton
                  size="small"
                  onClick={p.onClick}
                  disabled={p.isDisabled}
                  sx={tbBtn()}
                >
                  <NavigateNextRoundedIcon sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </GoToNextPage>

        <TbDivider />

        <ZoomOut>
          {(p: any) => (
            <Tooltip title="Zoom out">
              <IconButton size="small" onClick={p.onClick} sx={tbBtn()}>
                <ZoomOutRoundedIcon sx={{ fontSize: "1.15rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </ZoomOut>

        <Tooltip title="Fit to width">
          <IconButton
            size="small"
            onClick={() =>
              zoomPluginInstance.zoomTo(SpecialZoomLevel.PageWidth)
            }
            sx={tbBtn()}
          >
            <FitScreenIcon sx={{ fontSize: "1.15rem" }} />
          </IconButton>
        </Tooltip>

        <ZoomIn>
          {(p: any) => (
            <Tooltip title="Zoom in">
              <IconButton size="small" onClick={p.onClick} sx={tbBtn()}>
                <ZoomInRoundedIcon sx={{ fontSize: "1.15rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </ZoomIn>

        <TbDivider />

        <Tooltip title="Rotate left">
          <Box sx={pluginBtnWrap()}>
            <RotateBackwardButton />
          </Box>
        </Tooltip>
        <Tooltip title="Rotate right">
          <Box sx={pluginBtnWrap()}>
            <RotateForwardButton />
          </Box>
        </Tooltip>

        <TbDivider />

        <Tooltip title="Search">
          <IconButton
            size="small"
            onClick={() => setShowSearch((v) => !v)}
            sx={tbBtn(showSearch)}
          >
            <SearchRoundedIcon sx={{ fontSize: "1.15rem" }} />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
          <IconButton
            size="small"
            onClick={() => setIsFullscreen((v) => !v)}
            sx={tbBtn(isFullscreen)}
          >
            {isFullscreen ? (
              <FullscreenExitRoundedIcon sx={{ fontSize: "1.15rem" }} />
            ) : (
              <FullscreenRoundedIcon sx={{ fontSize: "1.15rem" }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Print">
          <Box sx={pluginBtnWrap()}>
            <PrintButton />
          </Box>
        </Tooltip>

        <Tooltip title="Download">
          <Box sx={pluginBtnWrap()}>
            <DownloadButton />
          </Box>
        </Tooltip>
      </Box>

      <Fade in={showSearch}>
        <Box
          sx={{
            display: showSearch ? "flex" : "none",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.75,
            backgroundColor: isDark
              ? "rgba(20,184,166,0.06)"
              : "rgba(20,184,166,0.04)",
            borderBottom: `1px solid ${c.toolbarBorder}`,
            flexShrink: 0,
          }}
        >
          <OutlinedInput
            inputRef={searchInputRef}
            size="small"
            placeholder="Search in document…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                jumpToNextMatch();
                setCurrentMatch((p) => (p >= matchCount ? 1 : p + 1));
              }
              if (e.key === "Escape") {
                setShowSearch(false);
                setSearchQuery("");
              }
            }}
            startAdornment={
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: "1rem", color: c.accent }} />
              </InputAdornment>
            }
            sx={{
              flex: 1,
              maxWidth: 340,
              fontSize: "0.85rem",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: c.inputBorder,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: c.accent,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: c.accent,
                borderWidth: "1px",
              },
              "& input": { color: c.text, py: "6px" },
              backgroundColor: c.inputBg,
              borderRadius: "8px",
            }}
          />
          <Tooltip title="Previous match">
            <span>
              <IconButton
                size="small"
                disabled={matchCount === 0}
                onClick={() => {
                  jumpToPreviousMatch();
                  setCurrentMatch((p) => (p <= 1 ? matchCount : p - 1));
                }}
                sx={tbBtn()}
              >
                <NavigateBeforeRoundedIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next match">
            <span>
              <IconButton
                size="small"
                disabled={matchCount === 0}
                onClick={() => {
                  jumpToNextMatch();
                  setCurrentMatch((p) => (p >= matchCount ? 1 : p + 1));
                }}
                sx={tbBtn()}
              >
                <NavigateNextRoundedIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
          {matchCount > 0 && (
            <Chip
              label={`${currentMatch} / ${matchCount}`}
              size="small"
              sx={{
                backgroundColor: c.active,
                color: c.accent,
                fontWeight: 600,
                fontSize: "0.72rem",
                height: "22px",
              }}
            />
          )}
          {searchQuery.trim() && matchCount === 0 && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "rgba(239,68,68,0.8)",
                whiteSpace: "nowrap",
              }}
            >
              No results
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => {
              setShowSearch(false);
              setSearchQuery("");
            }}
            sx={tbBtn()}
          >
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
      </Fade>

      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        <Fade in={showSidebar}>
          <Box
            sx={{
              display: showSidebar ? "flex" : "none",
              flexDirection: "column",
              width: "160px",
              flexShrink: 0,
              backgroundColor: c.sidebarBg,
              borderRight: `1px solid ${c.toolbarBorder}`,
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: isDark
                ? "rgba(255,255,255,0.10) transparent"
                : "#CBD5E1 transparent",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
              "& .rpv-thumbnail__item": {
                backgroundColor: "transparent !important",
                border: "none !important",
                padding: "8px 8px 4px !important",
              },
              "& .rpv-thumbnail__item--selected": {
                "& .rpv-thumbnail__cover": {
                  outline: `2px solid ${c.accent} !important`,
                  borderRadius: "4px",
                },
              },
              "& .rpv-thumbnail__cover": {
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.4) !important"
                  : "0 1px 6px rgba(0,0,0,0.12) !important",
                borderRadius: "4px !important",
              },
              "& .rpv-thumbnail__label": {
                color: `${c.muted} !important`,
                fontSize: "0.7rem !important",
                marginTop: "4px !important",
              },
            }}
          >
            <Thumbnails />
          </Box>
        </Fade>
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            backgroundColor: c.viewerBg,
            position: "relative",
            ".rpv-core__page-layer--single": {
              margin: "14px auto !important",
            },
            "& .rpv-core__inner-pages::-webkit-scrollbar": {
              width: 6,
              height: 6,
            },
            "& .rpv-core__inner-pages::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "& .rpv-core__inner-pages::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
            "& .rpv-core__inner-pages::-webkit-scrollbar-thumb:hover": {
              background: isDark ? "rgba(255,255,255,0.25)" : "#94a3b8",
            },

            ...(isDark && {
              "& .rpv-core__canvas-layer canvas": {
                filter: "brightness(0.88) contrast(1.04)",
              },
            }),
          }}
        >
          <Worker workerUrl="/pdf.worker.min.js">
            <Viewer
              fileUrl={blobUrl}
              plugins={[
                zoomPluginInstance,
                pageNavPluginInstance,
                getFilePluginInstance,
                searchPluginInstance,
                printPluginInstance,
                rotatePluginInstance,
                thumbnailPluginInstance,
                selectionModePluginInstance,
              ]}
              theme={theme.palette.mode}
              defaultScale={
                isMobile
                  ? SpecialZoomLevel.PageWidth
                  : defaultScale || SpecialZoomLevel.PageWidth
              }
            />
          </Worker>
        </Box>
      </Box>

      <Box sx={{ ...footerSx, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
          onClick={onClose}
          sx={cancelBtnSx}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default PDFViewer;
